/**
 * Where a question escalation is answered (agion/Agion#3446, challenge
 * 2026-10-26-escalation-question-chat-card Mid-Eval 1, ruling 2 of Mikko
 * Alasaarela): the in-thread card of the run that asked it. The Escalations
 * sidebar lists such a question as a header that opens that thread; the
 * card answers it. A form question keeps its answer in the sidebar, since
 * the card does not answer forms yet (agion/Agion#3437 adds them later).
 *
 * The addresses come from what governance serves on the escalation: its
 * mission, and the run named by metadata.execution_id (the runner's own
 * record), else the escalation's or the asker's execution. Nothing here
 * decides who may answer; the card reads that from governance.
 */

import { config } from '../utils/config'
import type { Escalation } from '../types/escalations'

function nonEmpty(value: unknown): value is string {
  return typeof value === 'string' && value !== ''
}

/** Whether the sidebar lists this escalation as a header that opens its run's thread. */
export function answeredInThread(escalation: Escalation): boolean {
  return escalation.escalation_type === 'question' && escalation.question?.type !== 'form'
}

/** The mission a question escalation belongs to, when governance names it. */
export function escalationMissionId(escalation: Escalation): string | undefined {
  const missionId = escalation.mission_id || escalation.question?.asker?.mission_id
  return nonEmpty(missionId) ? missionId : undefined
}

/** The run a question escalation belongs to, when governance names it. */
export function escalationExecutionId(escalation: Escalation): string | undefined {
  const fromMetadata = escalation.metadata?.['execution_id']
  if (nonEmpty(fromMetadata)) return fromMetadata
  const executionId = escalation.execution_id || escalation.question?.asker?.execution_id
  return nonEmpty(executionId) ? executionId : undefined
}

/**
 * The question headers the sidebar lists: open questions only (pending and
 * not past their expiry), newest first, so a new conversation is not
 * cluttered with stale questions from other runs.
 */
export function openQuestionHeaders(escalations: Escalation[], now: number = Date.now()): Escalation[] {
  const created = (e: Escalation) => {
    const at = Date.parse(e.created_at)
    return Number.isFinite(at) ? at : 0
  }
  return escalations
    .filter((e) => answeredInThread(e) && e.status === 'pending' && !(Date.parse(e.expires_at) <= now))
    .sort((a, b) => created(b) - created(a))
}

/**
 * Where a header opens: the run's chat thread in this app when the mission
 * and the run are known; otherwise the admin console, on the mission's
 * designer when the mission is known, else on the escalation in
 * Escalations.
 */
export type EscalationThreadTarget = { kind: 'thread'; to: string } | { kind: 'admin'; href: string }

export function escalationThreadTarget(escalation: Escalation): EscalationThreadTarget {
  const missionId = escalationMissionId(escalation)
  const executionId = escalationExecutionId(escalation)
  if (missionId && executionId) {
    return {
      kind: 'thread',
      to: `/mission/${encodeURIComponent(missionId)}?execution=${encodeURIComponent(executionId)}`,
    }
  }
  if (missionId) return { kind: 'admin', href: `${config.adminUrl}/missions/designer/${encodeURIComponent(missionId)}` }
  return { kind: 'admin', href: adminEscalationsHref(escalation.id) }
}

/** The admin console's Escalations page, opened on the escalation when one is named. */
export function adminEscalationsHref(escalationId?: string): string {
  const base = `${config.adminUrl}/governance/escalations`
  return escalationId ? `${base}?escalation=${encodeURIComponent(escalationId)}` : base
}

/**
 * Whether a header offers Answer in Escalations: the admin console's
 * Escalations page answers a question whose run has no in-thread card (a
 * channel or designer run) and decides who may. Hidden only when governance
 * marks the viewer can_decide false; where it is not served, admin-ui
 * decides.
 */
export function offersAnswerInEscalations(escalation: Escalation): boolean {
  return escalation.can_decide !== false
}
